import React, { useState } from 'react';
import MaterialIcon from 'react/elements/MaterialIcon';
import { eventFormFields, eventFromForm, eventTimeLabel, unixTime } from './calendarEventTime';
import { onlineOnly } from '../../hooks/useOffline';

let formCount = 0;

/**
 * A day's calendar events, opened from the day popup's Events row: each one a row that opens it for
 * editing in its place, and under them a form adding another. `events` are the food store's, with
 * their times in unix seconds. `onSave` takes what the api is sent (`saveCalendarEvent`) and
 * resolves whether it saved; a deletion is a save with `delete` set.
 *
 * Saving an event is online-only, so with `offline` the events can be read but not opened, added or
 * deleted: those controls are disabled, titled "Needs a connection".
 */
export default function DayEventsPanel({ date, events, busy = false, offline = false, onSave, onBack }) {
	const [editingId, setEditingId] = useState(null);
	const [error, setError] = useState(null);
	const save = (event) => {
		setError(null);
		return Promise.resolve(onSave(event)).then((saved) => {
			if(!saved) {
				setError(event.delete ? 'The event could not be deleted. Try again.' : 'The event could not be saved. Try again.');
			}
			return saved;
		});
	};
	const editing = events.find((event) => event.id === editingId) || null;

	return (
		<div className='day-meals-popup day-events'>
			<div className='day-meals-popup__picker-heading'>
				<button type='button' className='btn-flat day-meals-popup__back' onClick={onBack} aria-label='Back to the day'>
					<MaterialIcon>arrow_back</MaterialIcon>
				</button>
				<span>Events</span>
			</div>
			{error && <p className='day-meals-popup__error red-text'>{error}</p>}
			{events.length > 0 &&
			<ul className='day-events__list'>
				{events.map((event) => (
					<li key={event.id} className='day-events__event'>
						{event === editing ?
						<EventForm
							key={`edit-${event.id}`}
							date={date}
							event={event}
							busy={busy}
							offline={offline}
							onSave={(fields) => save({ ...fields, id: event.id }).then((saved) => {
								if(saved) {
									setEditingId(null);
								}
								return saved;
							})}
							onDelete={() => save({
								id: event.id,
								name: event.name,
								description: event.description,
								all_day: event.all_day,
								start_time: unixTime(event.start_time),
								end_time: unixTime(event.end_time),
								delete: true,
							})}
							onCancel={() => setEditingId(null)}
						/> :
						<button
							type='button'
							className='btn-flat day-events__event-button'
							disabled={busy}
							{...onlineOnly(offline)}
							onClick={() => {
								setError(null);
								setEditingId(event.id);
							}}
						>
							<MaterialIcon>event</MaterialIcon>
							<span className='day-events__event-content'>
								<span className='day-events__event-name'>{event.name}</span>
								<span className='day-events__event-detail'>
									{eventTimeLabel(event)}{event.description ? ` · ${event.description}` : ''}
								</span>
							</span>
							<MaterialIcon className='day-meals-popup__add'>edit</MaterialIcon>
						</button>
						}
					</li>
				))}
			</ul>
			}
			{!editing &&
			<>
				<div className='day-meals-popup__add-heading'>Add an event</div>
				<EventForm date={date} busy={busy} offline={offline} onSave={save} />
			</>
			}
		</div>
	);
}

/**
 * An event's name, when on the day it is, and a note. A new event's form empties once it saves, for the
 * next; an existing one's has Delete, which asks first, and Cancel.
 */
function EventForm({ date, event = null, busy, offline = false, onSave, onDelete = null, onCancel = null }) {
	const [fields, setFields] = useState(() => eventFormFields(event));
	const [problem, setProblem] = useState(null);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [id] = useState(() => `day-event-${++formCount}`);
	const set = (field) => (changeEvent) => {
		const value = changeEvent.target.type === 'checkbox' ? changeEvent.target.checked : changeEvent.target.value;
		setFields((current) => ({ ...current, [field]: value }));
	};
	const submit = (submitEvent) => {
		submitEvent.preventDefault();
		if(offline) {
			return;
		}
		const result = eventFromForm(date, fields);
		setProblem(result.error || null);
		if(result.error) {
			return;
		}
		onSave(result.event).then((saved) => {
			if(saved && !event) {
				setFields(eventFormFields());
			}
		});
	};

	if(confirmingDelete) {
		return (
			<div className='day-meals-popup__confirm' role='alertdialog' aria-label={`Delete ${event.name}`}>
				<p>Delete {event.name}?</p>
				<div className='day-meals-popup__confirm-buttons'>
					<button type='button' className='btn-flat day-meals-popup__confirm-cancel' onClick={() => setConfirmingDelete(false)}>
						Cancel
					</button>
					<button type='button' className='btn-flat day-meals-popup__confirm-remove' disabled={busy} {...onlineOnly(offline)} onClick={onDelete}>
						Delete
					</button>
				</div>
			</div>
		);
	}

	return (
		<form className={`day-events__form${event ? ' day-events__form--editing' : ''}`} onSubmit={submit}>
			<input
				id={`${id}-name`}
				type='text'
				className='browser-default day-events__name'
				placeholder='What is happening'
				aria-label='Event name'
				maxLength={100}
				autoFocus={!!event}
				{...onlineOnly(offline)}
				value={fields.name}
				onChange={set('name')}
			/>
			<label className='day-events__all-day'>
				<input type='checkbox' className='filled-in' checked={fields.allDay} onChange={set('allDay')} />
				<span>All day</span>
			</label>
			{!fields.allDay &&
			<div className='day-events__times'>
				<label htmlFor={`${id}-start`}>
					Starts
					<input id={`${id}-start`} type='time' className='browser-default day-events__start' value={fields.start} onChange={set('start')} />
				</label>
				<label htmlFor={`${id}-end`}>
					Ends
					<input id={`${id}-end`} type='time' className='browser-default day-events__end' value={fields.end} onChange={set('end')} />
				</label>
			</div>
			}
			<textarea
				className='browser-default day-meals-popup__note-input day-events__description'
				rows={2}
				placeholder='Notes'
				aria-label='Notes'
				value={fields.description}
				onChange={set('description')}
			/>
			{problem && <p className='day-events__problem red-text'>{problem}</p>}
			<div className='day-events__buttons'>
				{onDelete &&
				<button type='button' className='btn-flat day-events__delete' disabled={busy} {...onlineOnly(offline)} onClick={() => setConfirmingDelete(true)}>
					<MaterialIcon>delete</MaterialIcon>
					Delete
				</button>
				}
				{onCancel &&
				<button type='button' className='btn-flat day-events__cancel' onClick={onCancel}>
					Cancel
				</button>
				}
				<button type='submit' className='btn-flat day-events__save' disabled={busy} {...onlineOnly(offline)}>
					{event ? 'Save' : 'Add event'}
				</button>
			</div>
		</form>
	);
}
